/**
 * Utilities for working with localStorage
 */

/**
 * Save data to localStorage
 * @param key The key to save under
 * @param data The data to save
 * @returns true if save was successful, false otherwise
 */
export const saveToLocalStorage = <T>(key: string, data: T): boolean => {
  try {
    const serializedData = JSON.stringify(data);
    localStorage.setItem(key, serializedData);
    return true;
  } catch (error) {
    console.error(`Error saving to localStorage with key: ${key}`, error);
    return false;
  }
};

/**
 * Load data from localStorage
 * @param key The key to load from
 * @param defaultValue Default value to return if key doesn't exist
 * @returns The stored data or defaultValue if not found
 */
export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const serializedData = localStorage.getItem(key);
    if (serializedData === null) {
      return defaultValue;
    }
    return JSON.parse(serializedData) as T;
  } catch (error) {
    console.error(`Error loading from localStorage with key: ${key}`, error);
    return defaultValue;
  }
};

/**
 * Check if browser supports localStorage
 * @returns true if supported, false otherwise
 */
export const isLocalStorageSupported = (): boolean => {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}; 